<script lang="ts">
import {defineComponent} from 'vue';
import {pluralize} from './billing_utils.ts';
import $ from 'jquery';

export default defineComponent({
  name: 'AllSpiceBillingCommitterSettings',
  props: {
    seatsUsed: {
      type: Number,
      required: true,
      default: 0,
    },
    seatsMax: {
      type: Number,
      required: true,
      default: 0,
    },
    seatsEntitled: {
      type: Number,
      required: true,
      default: 0,
    },
    link: {
      type: String,
      required: false,
      default: '',
    },
    csrf: {
      type: String,
      required: false,
      default: '',
    },
    billingPortalLink: {
      type: String,
      required: true,
      default: '',
    },
    billingReadOnly: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      entitlementModel: 0,
      submitted: false,
      confirm: false,
      seatsUsedFormat: '',
      seatsOverMax: false,
    };
  },
  mounted() {
    this.entitlementModel = this.seatsEntitled;
    this.seatsUsedFormat = pluralize(this.seatsUsed, ' user', 'users');
  },
  methods: {

    /**
     * If input at 25, show warning about max seats
     * @param {number} value
     */
    showMaxWarning(value: number) {
      this.seatsOverMax = value >= this.seatsMax;
    },

    /**
     * Show the entitlement confirmation modal
     */
    showConfirmation() {
      // validate the entitlement value
      if (!(this.$refs.form as HTMLFormElement).reportValidity()) {
        return;
      }

      // check that there is a difference, open confirmation modal
      if (this.entitlementModel !== this.seatsEntitled) {
        this.confirm = true;
      }
    },

    /**
     * Hide the entitlement confirmation modal
     */
    cancelConfirmation() {
      this.confirm = false;
    },

    /**
     * Show the loading indicator
     */
    onSubmit() {
      this.confirm = false;
      this.submitted = true;
      // prevent the browser from showing a confirmation dialog about unsaved changes
      $(window).off('beforeunload');
      (this.$refs.form as HTMLFormElement).submit();
    },
  },
});
</script>

<template>
  <form
    ref="form"
    class="ui form"
    :action="link"
    method="post"
  >
    <div
      class="form-contents"
      :style="{filter:submitted ? 'opacity(25%)' : 'opacity(100%)'}"
      style="padding-bottom: 20px"
    >
      <input
        type="hidden"
        name="_csrf"
        :value="csrf"
      >
      <div
        v-show="confirm"
        class="confirmation-modal"
      >
        <div class="confirmation-contents">
          <h2>Confirmation</h2>
          <div v-if="entitlementModel !== seatsEntitled">
            <div class="warning">
              Are you sure you want to change your committers from {{ seatsEntitled }} to {{ entitlementModel }}?
            </div>
            <div class="details">
              This subscription change is prorated and will be {{ (entitlementModel > seatsEntitled) ? 'charged immediately' : 'credited on your next bill' }} based on the remaining time in the current billing cycle.
            </div>
          </div>
          <div class="bill-change">
            Visit the <a
              target="_blank"
              :href="billingPortalLink"
            >billing portal</a> to view the change to your bill.
          </div>
          <button
            ref="submitButton"
            class="ui primary button"
            :disabled="submitted"
            @click.stop.prevent="onSubmit"
          >
            Update
          </button>
          <button
            type="button"
            class="ui button"
            :disabled="submitted"
            @click.prevent="cancelConfirmation"
          >
            Cancel
          </button>
        </div>
      </div>
      <h4 class="ui top attached header">
        Committer Seats
      </h4>
      <div class="ui attached segment">
        <div
          v-show="seatsOverMax"
          class="ui visible center warning message"
        >
          Committer seats should be between {{ seatsUsed }} and {{ seatsMax }}. Need more seats? Please contact <a
            target="_blank"
            rel="noopener noreferrer"
            href="mailto:support@allspice.io"
          >support@allspice.io</a>.
        </div>
        <progress
          id="committers-progress"
          style="width: 65%"
          :value="seatsUsed"
          :max="seatsEntitled"
        />
        <label
          for="committers-progress"
          style="padding-left: 20px"
        >
          {{ seatsUsed }} of {{ seatsEntitled }} committer seats used
        </label>
        <div
          v-if="!billingReadOnly"
          class="loading-contents"
        >
          <div
            v-if="submitted"
            class="loading-container"
          >
            <div class="is-loading" />
            Loading...
          </div>
          <div class="ui divider" />
          <div
            class="inline field"
            style="width: 100%"
          >
            <input
              id="committers"
              v-model="entitlementModel"
              name="committers"
              type="number"
              :min="seatsUsed"
              :max="seatsMax"
              required
              :disabled="submitted"
              @input="event => showMaxWarning(Number((event.target as HTMLInputElement).value))"
            >
            <label
              style="font-size: 14px"
              for="committers"
            >seats</label>
          </div>
          <div class="details">
            If you would like to reduce your committer count to be less than {{ seatsUsedFormat }}, you must first remove committers from the organization.
          </div>
          <span>
            To update your billing information, change your billing frequency, or cancel a subscription, go to the <a
              target="_blank"
              :href="billingPortalLink"
            >billing portal</a>.
          </span>
        </div>
        <div
          v-else
          class="text grey italic"
          style="padding-top: 20px"
        >
          Contact <a
            target="_blank"
            rel="noopener noreferrer"
            href="mailto:support@allspice.io"
          >support@allspice.io</a> to increase or decrease accounts.
        </div>
        <div style="padding-top: 20px">
          <button
            v-if="!billingReadOnly"
            type="button"
            class="ui primary button"
            :disabled="confirm || submitted"
            @click.prevent="showConfirmation"
          >
            Update Committer Billing
          </button>
        </div>
      </div>
    </div>
  </form>
</template>

<style scoped>
.loading-container {
  position: absolute;
  width: 100%;
  text-align: center;
}
.loading-container .is-loading {
  height: 80px;
}
.confirmation-modal {
  z-index: 1000;
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
  background-color: var(--color-box-body);
  box-shadow: 1.5rem 2rem 3rem var(--color-shadow);
  color: var(--color-text);
  overflow: auto;
  position: absolute;
  width: 100%;
}
.confirmation-contents {
  padding: 30px;
}
.warning {
  font-size: 15px;
  display: block;
  padding-bottom: 10px;
}
.details {
  color: var(--color-text-light-2);
  display: block;
  padding-bottom: 20px;
}
.bill-change {
  padding-top: 35px;
  padding-bottom: 35px;
  font-size: 20px;
  font-weight: var(--font-weight-semibold);
}
</style>
